<template>
  <div class="plant-history-view-wrapper" data-cy="plant-history-view">
    <PlantHistorySidebar
      ref="sidebarRef"
      :searchInput="searchInput"
      :selectedSortOption="selectedSortOption"
      :sortAscending="sortAscending"
      :myVisitsOnly="myVisitsOnly"
      :cachePrefix="cachePrefix"
      :sortOptions="sortOptions"
      @selectedSortOptionChanged="selectedSortOption = $event"
      @sortAscendingChanged="sortAscending = $event"
      @myVisitsOnlyChanged="myVisitsOnly = $event"
      @searchSubmitted="onSearchSubmitted"
      @sortChanged="onSortChanged"
      @applyFilters="applyFilters"
      @resetFilters="resetFilters"
    />

    <div class="plant-history-view">
      <IngredientDetailsModal
        v-if="selectedJobItem?.Type === EnumPlantHistoryType.IngredientLineItem"
        :jobId="selectedJobItem.JobId"
        :companyId="selectedJobItem.CompanyId.toString()"
        @closeModal="selectedJobItem = null"
      />
      <ProductDetailsModal
        v-if="selectedJobItem?.Type === EnumPlantHistoryType.ProductLineItem"
        :jobId="selectedJobItem.JobId"
        :companyId="selectedJobItem.CompanyId.toString()"
        @closeModal="selectedJobItem = null"
      />

      <div class="plant-history-view__header">
        <h1 class="header-title">Plant History</h1>
        <div class="header-content">
          <div class="header-item">
            <div class="header-label">Plant & Type</div>
            <div class="header-value">
              <div class="plant-name">
                {{ plant ? plant.Name : "No Plant Found" }}
              </div>
              <div class="plant-type">{{ plant?.Type }}</div>
            </div>
          </div>
          <div class="header-item">
            <div class="header-label">Location</div>
            <div class="header-value">{{ plant?.PhysicalAddress }}</div>
          </div>
          <div class="header-item">
            <div class="header-label">RFR Name</div>
            <div class="header-value">{{ plant?.RfrName }}</div>
          </div>
          <div v-if="plant?.Categories && plant.Categories.length > 0" class="header-item">
            <div class="header-label">Categories</div>
            <div class="header-value categories">{{ plant?.Categories?.join(" | ") }}</div>
          </div>
          <div v-if="plant?.PlantStatus && plant.PlantStatus.length > 0" class="header-item full">
            <div class="header-label">Companies</div>
            <div class="plant-status-list">
              <div v-for="(status, index) in plant.PlantStatus" :key="index" class="plant-status-item">
                <div class="header-value">
                  {{ status.Key }} <span class="plant-status-value"> [{{ status.Value }}] </span>
                  <span v-if="status.StartDate" class="plant-status-value">
                    [As of {{ new Date(status.StartDate).toLocaleDateString() }}]</span
                  >
                </div>
                <span v-if="index < plant.PlantStatus.length - 1" class="separator">•</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <PlantHistoryList
        :items="historyItems"
        :total="total"
        :skip="skip"
        :defaultTake="take"
        :loading="loading"
        :usePageScroll="true"
        @itemsChanged="
          (newSkip: number, newTake: number) => {
            skip = newSkip;
            take = newTake;
            getPlantHistoryAsync(true);
          }
        "
        @itemClicked="onItemClicked"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { api } from "@/api";
import type { GetPlantHistoryWithFilteringRequest } from "@/api/plantHistory";
import type { PlantHistoryDisplay } from "@/Domain/PlantHistoryDisplay";
import type { PlantDisplay } from "@/Domain/PlantDisplay";
import { EnumPlantHistoryType } from "@/Domain/EnumPlantHistoryType";
import { handleResponsePromiseAsync } from "@/shared/helpers/apiHelper";
import { SessionCacheService } from "@/shared/services/SessionCacheService";
import { onMounted, ref, watch } from "vue";
import PlantHistoryList from "./PlantHistoryList.vue";
import PlantHistorySidebar from "./PlantHistorySidebar.vue";
import { useMainStore } from "@/stores/useMainStore";
import type { FilterExpression } from "@/Domain/FilterExpression";
import IngredientDetailsModal from "../submissionsOverview/IngredientDetailsModal.vue";
import ProductDetailsModal from "../submissionsOverview/ProductDetailsModal.vue";

const main = useMainStore();

// Get plantId from route param
const props = defineProps<{
  plantId: number;
}>();

const plant = ref<PlantDisplay | null>(null);
const historyItems = ref<PlantHistoryDisplay[]>([]);
const total = ref<number>(0);
const skip = ref<number>(0);
const take = ref<number>(25);
const loading = ref<boolean>(false);
const plantLoaded = ref<boolean>(false);

const selectedJobItem = ref<PlantHistoryDisplay | null>(null);

const cachePrefix = "PlantHistory_";

const sidebarRef = ref<InstanceType<typeof PlantHistorySidebar>>();

const actionsExpanded = ref<boolean>(SessionCacheService.get(`${cachePrefix}actionsExpanded`) ?? true);
const sortAscending = ref<boolean>(SessionCacheService.get(`${cachePrefix}sortAscending`) ?? false);
const searchInput = ref<string>(SessionCacheService.get(`${cachePrefix}searchInput`) ?? "");
const myVisitsOnly = ref<boolean>(SessionCacheService.get(`${cachePrefix}myVisitsOnly`) ?? false);

const filters = ref<FilterExpression>();

const sortOptions = [
  { text: "Sort By", value: "" },
  { text: "Date", value: "Date" },
  { text: "Last Updated", value: "LastUpdateDate" },
  { text: "Visit Type", value: "VisitType" },
  { text: "RFR Name", value: "RfrName" },
];

const selectedSortOption = ref<{ text: string; value: string }>(
  SessionCacheService.get(`${cachePrefix}sortOption`) ?? sortOptions[0]
);

const getRequestParameters = function (
  valuesForFieldOnly: string = ""
): GetPlantHistoryWithFilteringRequest | undefined {
  return {
    userLogin: main.userLogin,
    plantId: props.plantId,
    beginDate: sidebarRef.value?.filtersRef?.beginDate?.toLocaleDateString("en-US") ?? "",
    endDate: sidebarRef.value?.filtersRef?.endDate?.toLocaleDateString("en-US") ?? "",
    filters: filters.value ? JSON.stringify(filters.value) : null,
    searchString: searchInput.value,
    skip: skip.value,
    take: take.value,
    valuesForFieldOnly: valuesForFieldOnly,
    sortField: selectedSortOption.value.value,
    ascending: sortAscending.value,
    getOtherRfrsVisits: !myVisitsOnly.value,
  } as GetPlantHistoryWithFilteringRequest;
};

const getPlantAsync = async function () {
  await handleResponsePromiseAsync(
    api.get("GetPlant", {
      plantId: props.plantId,
    }),
    (data) => {
      plant.value = data;
    },
    "PlantHistoryView.vue",
    "getPlantAsync"
  );
  plantLoaded.value = true;
};

const getPlantHistoryAsync = async function (append: boolean = false) {
  if (loading.value) return;
  loading.value = true;
  if (!append) historyItems.value = [];

  const requestParams = getRequestParameters();
  if (!requestParams) {
    loading.value = false;
    return;
  }

  await handleResponsePromiseAsync(
    api.get("GetPlantHistoryWithFiltering", requestParams),
    (data) => {
      if (!append) {
        historyItems.value = data.ListItems;
      } else {
        historyItems.value = [...historyItems.value, ...data.ListItems];
      }
      total.value = data.TotalItemsInList;
    },
    "PlantHistoryView.vue",
    "getPlantHistoryAsync"
  );

  loading.value = false;
};

const onSearchSubmitted = async function (searchValue: string) {
  searchInput.value = searchValue;
  skip.value = 0;
  SessionCacheService.set(`${cachePrefix}searchInput`, searchValue);
  await getPlantHistoryAsync();
};

const onSortChanged = function (option?: { text: string; value: string }) {
  if (option) selectedSortOption.value = option;

  skip.value = 0;
  SessionCacheService.set(`${cachePrefix}sortAscending`, sortAscending.value);
  SessionCacheService.set(`${cachePrefix}sortOption`, selectedSortOption.value);
  getPlantHistoryAsync();
};

const applyFilters = async function (newFilters: FilterExpression) {
  searchInput.value = "";
  skip.value = 0;
  filters.value = newFilters;
  SessionCacheService.set(`${cachePrefix}searchInput`, "");
  await getPlantHistoryAsync();
};

const resetFilters = async function () {
  searchInput.value = "";
  skip.value = 0;
  SessionCacheService.set(`${cachePrefix}searchInput`, "");
  await getPlantHistoryAsync();
};

const onItemClicked = function (item: PlantHistoryDisplay) {
  // Handle item click based on type
  if (item.Type === EnumPlantHistoryType.VisitInspection) {
    window.open(
      `/oudirect/inspections/RfrVisitInspectionForm?category=Inspection&mode=view&visitID=${item.Id}&menu=VisitsAsRfr`,
      "_blank",
      "noopener,noreferrer"
    );
  } else if (item.Type === EnumPlantHistoryType.VisitComment) {
    window.open(
      `/oudirect/inspections/RfrVisitInspectionForm?category=Inspection&mode=view&visitID=${item.VisitId}&menu=VisitsAsRfr`,
      "_blank",
      "noopener,noreferrer"
    );
  } else if (
    item.Type === EnumPlantHistoryType.IngredientLineItem ||
    item.Type === EnumPlantHistoryType.ProductLineItem
  ) {
    selectedJobItem.value = item;
  }
};

watch(
  () => [sortAscending.value],
  () => {
    onSortChanged();
  }
);

watch(
  () => actionsExpanded.value,
  () => {
    SessionCacheService.set(`${cachePrefix}actionsExpanded`, actionsExpanded.value);
  }
);

watch(
  () => myVisitsOnly.value,
  async () => {
    skip.value = 0;
    SessionCacheService.set(`${cachePrefix}myVisitsOnly`, myVisitsOnly.value);
    await getPlantHistoryAsync();
  }
);

onMounted(async () => {
  if (sidebarRef.value?.filtersRef) {
    filters.value = sidebarRef.value.filtersRef.getFilterExpression();
  }
  await getPlantAsync();
  await getPlantHistoryAsync();
});
</script>

<style lang="scss">
.plant-history-view-wrapper {
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 24px;
  position: relative;
  max-width: 1300px;
  margin: 30px auto 0;
  padding: 0 15px;

  @include media-breakpoint-up(lg) {
    flex-direction: row;
    align-items: flex-start;
  }
}

.plant-history-view {
  display: flex;
  flex-direction: column;
  height: 100%;
  flex: 1;
  overflow: hidden;
  width: 100%;
  gap: 24px;

  &__header {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 24px;
    background: #244880;
    border-radius: 8px;
    box-shadow:
      0 4px 6px rgba(0, 0, 0, 0.1),
      0 2px 4px rgba(0, 0, 0, 0.06);

    .header-title {
      color: #ffffff;
      font-size: 20px;
      font-weight: bold;
    }

    .header-content {
      display: grid;
      grid-template-columns: 2fr 1.5fr 1fr 2fr;
      gap: 24px;
      border-top: 1px solid rgba(255, 255, 255, 0.2);
      padding-top: 8px;
    }

    .header-item {
      display: flex;
      flex-direction: column;

      &.full {
        grid-column: 1 / -1;
      }
    }

    .header-label {
      font-size: 11px;
      font-weight: 600;
      color: #dbeafe;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }

    .header-value {
      font-size: 14px;
      color: #ffffff;

      .plant-name {
        font-weight: 600;
        font-size: 18px;
      }

      .plant-type {
        font-weight: 600;
        font-size: 16px;
        color: #dbeafe;
      }

      .status-certified {
        color: #86efac;
      }

      .status-pending {
        color: #fcd34d;
      }

      .status-terminated {
        color: #fca5a5;
      }

      &.categories {
        font-weight: 600;
        font-size: 14px;
      }
    }

    .plant-status-list {
      display: flex;
      flex-direction: row;
      flex-wrap: wrap;

      .separator {
        color: rgba(219, 234, 254, 0.6);
        font-weight: 400;
        padding: 0 8px;
      }
    }

    .plant-status-item {
      display: flex;
      align-items: center;
    }

    .plant-status-value {
      font-weight: 600;
      color: #dbeafe;
    }

    @media (max-width: 768px) {
      grid-template-columns: 1fr;
      gap: 12px;
      padding: 12px;

      .header-title {
        h1 {
          font-size: 24px;
          margin: 0 0 12px 0;
        }
      }

      .header-content {
        grid-template-columns: 1fr;
        gap: 12px;
      }
    }
  }

  &__list {
    flex: 1 1 100%;
    overflow-y: auto;
    padding: 20px;
    background: #f9fafb;

    .history-list {
      display: flex;
      flex-direction: column;
      gap: 12px;
    }

    .loader {
      display: flex;
      justify-content: center;
      padding: 48px;
    }

    .no-results {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 80px 16px;
      text-align: center;

      &-icon {
        font-size: 3.5rem;
        color: #9ca3af;
        margin-bottom: 20px;
      }

      &-text {
        font-size: 1.5rem;
        font-weight: 600;
        color: #374151;
        margin-bottom: 12px;
      }

      p {
        color: #6b7280;
        font-size: 1rem;
        line-height: 1.5;
      }
    }

    @media (max-width: 768px) {
      padding: 16px;
    }
  }
}
</style>
